import { TestRunFlagsSchema } from './testRunFlags'
import { BugFixResultSchema } from '../../netlify/functions/lib/github-app/bug-fix-policy'
import { decodeComparisonTrees, StoredComparisonTreeSchema } from '../lib/comparisonTreeDiffs'
import { InteractionSchema } from './sessionInteraction'
import { SecurityHistoryEntrySchema } from './securityReactivation'
import { SecurityTargetSourceSchema } from '../guidance/polish/security/scan-records'
import { z } from 'zod'
import { TestRunOrderItemSchema } from '../lib/githubTestRun'

const JourneyStepsSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.unknown()),
  z.record(z.string(), z.unknown()),
])

const TestSessionExplorationSchema = z.object({
  blocked_kind: z.string().nullable().optional(),
  blocked_detail: z.string().nullable().optional(),
  id: z.string(),
  prompt: z.string(),
  status: z.string(),
  task_id: z.string().nullable(),
}).passthrough()

const TestSessionJourneyRunSchema = z.object({
  id: z.string(),
  attempt_ids: z.array(z.string()),
  journey_id: z.string().nullable(),
  journey_name: z.string().nullable(),
  goal: z.string().nullable(),
  status: z.string(),
  error: z.string().nullable().default(null),
  task_id: z.string().nullable().default(null),
  priority: z.number().nullable().default(null),
  queue_rank: z.string().nullable().default(null),
  bugs_found: z.number().default(0),
}).passthrough()

const TestSessionBugSchema = z.object({
  is_duplicate: z.boolean().default(false),
  id: z.string(),
  title: z.string(),
  severity: z.string(),
  status: z.string(),
  test_run_id: z.string().nullable().default(null),
}).passthrough()

export const TestSessionSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  version_id: z.string().nullable(),
  ordinal: z.number(),
  flags: TestRunFlagsSchema.default({}),
  status: z.string(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  prLabel: z.string().nullable(),
  version_task_id: z.string().nullable().default(null),
  title: z.string(),
  explorations: z.array(TestSessionExplorationSchema),
  journeyRuns: z.array(TestSessionJourneyRunSchema),
  bugs: z.array(TestSessionBugSchema),
  findings: z.object({ new_bugs: z.array(TestSessionBugSchema), rediscoveries: z.array(TestSessionBugSchema) }).optional(),
}).passthrough()

const JourneyVersionSchema = z.object({
  id: z.string(),
  journey_id: z.string(),
  version_number: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  instructions: z.string().nullable(),
  steps: JourneyStepsSchema,
  summary: z.string().nullable(),
  step_count: z.number(),
  exploration_id: z.string().nullable(),
  created_at: z.string(),
  last_run_status: z.string().nullable(),
  last_run_at: z.string().nullable(),
  last_run_error: z.string().nullable(),
  run_count: z.number(),
  last_run_bug_id: z.string().nullable(),
  last_run_bug_status: z.string().nullable(),
  last_run_bug_title: z.string().nullable(),
}).passthrough()

export const JourneySchema = z.object({
  id: z.string(),
  project_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  instructions: z.string().nullable(),
  target_url: z.string().nullable(),
  use_saved_session: z.boolean().default(true),
  mobile: z.boolean(),
  polish: z.boolean(),
  parent_id: z.string().nullable(),
  source_session_id: z.string().nullable().optional(),
  child_count: z.number(),
  steps: JourneyStepsSchema,
  summary: z.string().nullable(),
  step_count: z.number(),
  versions: z.array(JourneyVersionSchema),
  judge_status: z.string().nullable().optional(),
  judge_submissions: z.any().nullable().optional(),
  test_run_count: z.number().optional(),
  last_run_id: z.string().nullable().optional(),
  preview_run_id: z.string().nullable().optional(),
  last_run_at: z.string().nullable().optional(),
  last_run_status: z.string().nullable().optional(),
  queue_task_id: z.string().nullable().optional(),
  queue_priority: z.number().nullable().optional(),
  queue_rank: z.string().nullable().optional(),
  last_run_bug_id: z.string().nullable().optional(),
  last_run_bug_status: z.string().nullable().optional(),
  last_run_bug_title: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
}).passthrough()

// Journey list responses deliberately omit the opaque step blob from each version. The list cards
// only need the version metadata, while the detail endpoint returns the complete version shape.
// Keeping this schema separate prevents a paginated list from either rejecting valid slim rows or
// loading every journey's potentially large steps payload just to render the grid.
const JourneyListVersionSchema = JourneyVersionSchema.omit({ steps: true })
const JourneyListItemSchema = JourneySchema.extend({
  versions: z.array(JourneyListVersionSchema),
})

export const PaginatedJourneysResponseSchema = z.object({
  items: z.array(JourneyListItemSchema),
  total: z.number(),
  totalAll: z.number().optional(),
  statusCounts: z.record(z.string(), z.number()).optional(),
  page: z.number(),
  pageSize: z.number(),
}).passthrough()

export const ReorderJourneyQueueResponseSchema = z.object({
  moved: z.object({
    task_id: z.string(),
    priority: z.number(),
    queue_rank: z.string(),
  }),
}).passthrough()

export const BulkJourneyQueueResponseSchema = z.object({
  affected_task_ids: z.array(z.string()),
}).passthrough()

// A COLLAPSED pull-request row — the Test Runs list and the GitHub tab both render PRs at this depth.
// The arrays a row reveals when expanded are deliberately absent; they arrive from the per-PR detail
// response (GithubPullRequestDetailResponseSchema) so a list never carries detail nobody has opened.
export const GithubPullRequestSummarySchema = z.object({
  pr_number: z.number(),
  repo_full_name: z.string(),
  pr_url: z.string(),
  pr_title: z.string().nullable(),
  branch_name: z.string(),
  integration_source: z.string(),
  head_sha: z.string(),
  run_status: z.string(),
  pr_state: z.string(),
  version_id: z.string(),
  task_id: z.string().nullable(),
  version_status: z.string(),
  deployed_url: z.string().nullable(),
  awaiting_deployment: z.boolean(),
  awaiting_deployment_since: z.string(),
  replay_recording_id: z.string().nullable(),
  approval: z.enum(['approved', 'blocked', 'pending', 'cancelled']),
  bug_count: z.number(),
  journeys_count: z.number(),
  bugs: z.array(z.unknown()),
  updated_at: z.string(),
  created_at: z.string(),
}).passthrough()

export const TestSessionsPageResponseSchema = z.object({
  items: z.array(TestSessionSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  test_run_order: z.array(TestRunOrderItemSchema).optional(),
  // Collapsed-row summaries for the GitHub PRs on THIS page, so the Test Runs list renders a page of
  // rows in one request instead of waiting on a second project-wide pull-request fetch.
  github_prs: z.array(GithubPullRequestSummarySchema).optional(),
}).passthrough()

export const TestSessionsResponseSchema = z.object({
  items: z.array(TestSessionSchema),
}).passthrough()

export const TestSessionResponseSchema = z.object({ session: TestSessionSchema.nullable() })

// The prose QA wrote about one round of testing — 'started' when the round was kicked off, 'finished'
// when it went idle — rendered as markdown context above that run's results. Either key may be null:
// a 'started' notice is stored against the version only, because the round's session does not exist
// yet when it is written. See netlify/functions/lib/test-run-notices.ts.
export const TestRunNoticeSchema = z.object({
  source: z.string().optional(),
  id: z.string(),
  kind: z.enum(['started', 'findings', 'coverage', 'finished', 'obstacles']),
  content: z.string(),
  version_id: z.string().nullable(),
  session_id: z.string().nullable(),
  created_at: z.string(),
}).passthrough()

export const TestRunNoticesResponseSchema = z.object({
  notices: z.array(TestRunNoticeSchema),
}).passthrough()

// The project API intentionally keeps these response schemas next to the list schemas. Every value
// crossing the RTK Query fetch boundary is parsed here before a component can read it.
export const ProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  target_url: z.string().nullable(),
  status: z.string(),
  // Stored as null when a project was created with no passes and omitted by the list summary
  // projection, yet the Project type declares it required and pages read it unguarded — normalize
  // at the fetch boundary so no served body can crash a page on it.
  enabled_polish_passes: z.array(z.string()).nullish().transform((passes) => passes ?? []),
}).passthrough()

// Public, unauthenticated project summary served by /api/invite-info for the invite landing page.
// Only brand-safe display fields — never QA data. `name`/`thumbnailUrl` are null when the project has
// no name / no captured frame yet.
export const InviteInfoSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
}).passthrough()

export const IssueTrackerTestResponseSchema = z.object({
  success: z.boolean().optional(),
  detail: z.string().optional(),
  error: z.string().optional(),
}).passthrough()

export const ProjectActionResponseSchema = z.object({
  success: z.boolean().optional(),
  detail: z.string().optional(),
  error: z.string().optional(),
}).passthrough()

export const GithubAppStatusSchema = z.object({
  appConfigured: z.boolean(),
  slug: z.string(),
  repoFullName: z.string().nullable(),
  installed: z.boolean(),
  installationId: z.string().nullable(),
  ownerInstalled: z.boolean(),
  ownerInstallationId: z.string().nullable(),
  existingProject: z.object({ id: z.string(), name: z.string() }).nullable(),
  defaultBranch: z.string().nullable(),
}).passthrough()

export const GithubAppInstallationSchema = z.object({
  installationId: z.string(),
  accountLogin: z.string().nullable(),
  suspended: z.boolean(),
  repositories: z.array(z.object({
    fullName: z.string(),
    private: z.boolean(),
    defaultBranch: z.string(),
    htmlUrl: z.string(),
    repoKey: z.string().nullable(),
    existingProjectId: z.string().nullable(),
  }).passthrough()),
}).passthrough()

export const ReverseProxyEventsResponseSchema = z.object({
  enabled: z.boolean().optional(),
  events: z.array(z.object({
    id: z.number(),
    type: z.enum(['provisioning', 'server_up', 'server_down', 'client_connected', 'client_disconnected']),
    detail: z.string().nullable(),
    at: z.string(),
  }).passthrough()),
}).passthrough()

export const ProjectMembersResponseSchema = z.object({
  items: z.array(z.object({
    id: z.string(),
    project_id: z.string(),
    user_id: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable().optional(),
    role: z.string(),
    created_at: z.string(),
  }).passthrough()),
  role: z.string(),
}).passthrough()

const TeamRoleSchema = z.enum(['owner', 'admin', 'member'])

// The teams the signed-in user belongs to (/api/teams). `invite_url` is the bearer self-join secret
// in URL form, so the server only fills it in for a role allowed to hand out access.
export const TeamsResponseSchema = z.object({
  items: z.array(z.object({
    id: z.string(),
    name: z.string(),
    role: TeamRoleSchema,
    member_count: z.number(),
    is_owner: z.boolean(),
    can_manage: z.boolean(),
    can_manage_billing: z.boolean(),
    invite_url: z.string().nullable(),
  }).passthrough()),
}).passthrough()

export const TeamMembersResponseSchema = z.object({
  items: z.array(z.object({
    id: z.string(),
    team_id: z.string(),
    user_id: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable().optional(),
    role: TeamRoleSchema,
    created_at: z.string(),
  }).passthrough()),
  role: TeamRoleSchema,
}).passthrough()

// Public, unauthenticated team summary served by /api/team-invite-info for the /join/:token landing.
export const TeamInviteInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  member_count: z.number(),
}).passthrough()

export const TeamInviteUrlResponseSchema = z.object({
  invite_url: z.string().nullable(),
}).passthrough()

const CommentTargetTypeSchema = z.enum(['bug', 'test_run'])

export const CommentSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  target_type: CommentTargetTypeSchema,
  target_id: z.string(),
  author_id: z.string(),
  author_name: z.string().nullable(),
  author_email: z.string().nullable(),
  content: z.string(),
  mentioned_user_ids: z.array(z.string()).default([]),
  created_at: z.string(),
}).passthrough()

export const CommentsResponseSchema = z.object({
  items: z.array(CommentSchema),
}).passthrough()

export const AppNotificationSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  project_id: z.string(),
  type: z.enum(['comment', 'mention']),
  target_type: CommentTargetTypeSchema,
  target_id: z.string(),
  comment_id: z.string().nullable(),
  actor_name: z.string().nullable(),
  content: z.string().nullable(),
  read_at: z.string().nullable(),
  created_at: z.string(),
}).passthrough()

export const NotificationsResponseSchema = z.object({
  items: z.array(AppNotificationSchema),
  unread: z.number(),
}).passthrough()

export const ExplorationSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  prompt: z.string(),
  status: z.string(),
  journeys_created: z.number(),
  journeys_updated: z.number(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
}).passthrough()

export const PaginatedExplorationsResponseSchema = z.object({
  items: z.array(ExplorationSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
}).passthrough()

export const TestRunOutcomeSchema = z.object({
  status: z.string(), stepIndex: z.number().int().nonnegative().optional(), detail: z.string().optional(),
  reason: z.string().optional(), proposedBug: z.string().nullable().optional(), judgeProblems: z.array(z.string()).optional(),
}).passthrough()

export const TestRunSchema = z.object({
  outcome: TestRunOutcomeSchema.nullable().optional(),
  id: z.string(),
  project_id: z.string(),
  journey_id: z.string().nullable(),
  status: z.string(),
  bugs_found_count: z.number(),
  created_at: z.string(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
}).passthrough()

export const PaginatedTestRunsResponseSchema = z.object({
  items: z.array(TestRunSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
}).passthrough()

export const ChatMessagesResponseSchema = z.object({
  messages: z.array(z.object({
    id: z.string(),
    role: z.enum(['user', 'assistant']),
    content: z.string(),
    created_at: z.string(),
    source: z.string().nullable().optional(),
    author_id: z.string().nullable().optional(),
    author_name: z.string().nullable().optional(),
  }).passthrough()),
}).passthrough()

export const BugCountsSchema = z.object({
  total: z.number(),
  journeyBugs: z.number(),
  polishBugs: z.number(),
  openCount: z.number(),
  resolvedCount: z.number(),
}).passthrough()

export const ProjectInsightsResponseSchema = z.object({
  projects: z.array(z.object({
    project_id: z.string(),
    bugCounts: z.object({
      total: z.number(),
      journeyBugs: z.number(),
      polishBugs: z.number(),
      openCount: z.number(),
      resolvedCount: z.number(),
    }).passthrough(),
    severityCounts: z.record(z.string(), z.number()),
    polishCategories: z.record(z.string(), z.number()),
    journeyCount: z.number(),
    explorationCount: z.number(),
  }).passthrough()),
}).passthrough()

const BugImpactAnalysisSchema = z.object({ impact: z.string().nullish() }).passthrough()

export const BugFixPrSchema = z.object({
  verification_detail: z.string().nullable(),
  repo_full_name: z.string(), pr_number: z.number(), active: z.boolean(),
  merged_at: z.string().nullable(), merge_commit_sha: z.string().nullable(),
  head_sha: z.string().nullable(), version_id: z.string().nullable(), pr_state: z.string().nullable(),
})

export const BugSchema = z.object({
  fix_prs: z.array(BugFixPrSchema).optional(),
  analysis: BugImpactAnalysisSchema.nullish(),
  id: z.string(),
  project_id: z.string(),
  severity: z.string(),
  title: z.string(),
  status: z.string(),
  description: z.string().nullable().optional(),
  test_run_id: z.string().nullable().optional(),
  journey_id: z.string().nullable().optional(),
  polish_category: z.string().nullable().optional(),
}).passthrough()

export const PaginatedProjectsResponseSchema = z.object({
  items: z.array(ProjectSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
}).passthrough()

export const ReverseProxyStatusSchema = z.object({
  configured: z.boolean(),
  state: z.string(),
  ready: z.boolean(),
  frpc_connected: z.boolean(),
  client_health: z.unknown().nullable(),
  tunnel: z.unknown().nullable(),
  enabled: z.boolean().optional(),
  reverse_proxy_status: z.string().optional(),
  target_url: z.string().nullable().optional(),
  environment: z.unknown().nullable().optional(),
  error: z.string().optional(),
}).passthrough()

export const PaginatedBugsResponseSchema = z.object({
  items: z.array(BugSchema),
  total: z.number(),
  resolvedCount: z.number().optional(),
  page: z.number(),
  pageSize: z.number(),
}).passthrough()

export const BugBreakdownSchema = z.array(z.object({
  category: z.string(),
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
}).passthrough())

const TaskSummarySchema = z.object({
  id: z.string(),
  project_id: z.string(),
  test_run_id: z.string().nullable(),
  journey_id: z.string().nullable(),
  exploration_id: z.string().nullable(),
  goal: z.string().nullable(),
  status: z.string(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
}).passthrough()

const TaskSchema = TaskSummarySchema.extend({
  container_name: z.string().nullable().optional(),
  container_url: z.string().nullable().optional(),
}).passthrough()

const TaskHistoryStatsSchema = z.object({
  total: z.number(),
  completed: z.number(),
  failed: z.number(),
  total_duration_seconds: z.number(),
  avg_duration_seconds: z.number(),
}).passthrough()

export const ProcessingSummarySchema = z.object({
  running: z.array(TaskSchema),
  queued: z.array(TaskSchema),
  max_containers: z.number(),
}).passthrough()

export const ProjectTaskActivitySchema = z.object({
  stats: TaskHistoryStatsSchema,
  recent: z.array(TaskSummarySchema),
}).passthrough()

export const TaskHistorySchema = z.object({
  tasks: z.array(TaskSummarySchema),
  stats: TaskHistoryStatsSchema,
  page: z.number(),
  pageSize: z.number(),
}).passthrough()

const SecurityAuditCoverageSchema = z.object({
  passive_reviewed: z.boolean(),
  active_probed: z.boolean(),
  passive_limited: z.boolean(),
  active_limited: z.boolean(),
})

const SecurityAuditResultSchema = z.object({
  control: z.string(),
  title: z.string(),
  status: z.enum(['exception', 'no-exceptions-observed', 'limited', 'not-tested']),
  finding_count: z.number(),
  finding_kinds: z.array(z.string()),
  tests: z.array(z.string()),
  limitation: z.string(),
})

const SecurityProbeRequestSchema = z.object({
  method: z.string(), url: z.string(), status: z.number().nullable(), narration: z.string(),
  duration_ms: z.number().optional(), error: z.string().optional(), operation: z.string().optional(),
})

const SecurityProbeGroupSchema = z.object({
  operation: z.string(), label: z.string(), description: z.string(), request_count: z.number(),
  requests: z.array(SecurityProbeRequestSchema).optional(), warnings: z.array(z.string()),
  bug_ids: z.array(z.string()),
  findings: z.array(z.object({ kind: z.string(), severity: z.string(), element: z.string().nullable(), description: z.string().nullable(), bug_ids: z.array(z.string()) })),
})

export const ProjectSecurityResultsSchema = z.object({
  history: z.array(SecurityHistoryEntrySchema).default([]),
  request_details_included: z.boolean(),
  scans: z.array(z.object({
    id: z.string(), status: z.string(), reason: z.string().nullable(), created_at: z.string(),
    started_at: z.string().nullable(), completed_at: z.string().nullable(), test_session_id: z.string(),
    security_bug_count: z.number(),
    targets_identified: z.number(), requests_made: z.number(), verified_origins: z.array(z.string()),
    targets: z.array(z.object({
      url: z.string(), method: z.string(), params: z.array(z.string()), id_params: z.array(z.string()),
      source: SecurityTargetSourceSchema.optional(),
      probe_groups: z.array(SecurityProbeGroupSchema), request_count: z.number(), bug_ids: z.array(z.string()),
    })),
    general_groups: z.array(SecurityProbeGroupSchema),
    request_details_available: z.number(),
    ownership_waived: z.boolean(), identities_configured: z.number(), identities_acquired: z.array(z.string()),
    stopped_reason: z.string().nullable(), warnings: z.array(z.string()),
    findings: z.array(z.object({ kind: z.string(), severity: z.string(), element: z.string().nullable(), description: z.string().nullable() })),
    audit_coverage: SecurityAuditCoverageSchema,
    audit_results: z.array(SecurityAuditResultSchema),
  })),
  bugs: z.array(BugSchema),
})

export const GithubPullRequestDetailResponseSchema = z.object({
  pr: GithubPullRequestSummarySchema.extend({
    bug_verifications: z.array(BugFixResultSchema).default([]),
    journey_changes: z.array(z.unknown()),
    screenshots: z.array(z.unknown()),
  }).passthrough(),
}).passthrough()

const VersionLogSchema = z.object({
  id: z.string(),
  task_id: z.string().nullable().optional(),
  message: z.string(),
  log_type: z.string(),
  created_at: z.string(),
}).passthrough()

const VersionJourneyChangeSchema = z.object({
  journey_id: z.string().nullable(),
  journey_name: z.string().nullable(),
  action: z.enum(['created', 'updated', 'deleted']),
  version_number: z.number().nullable(),
  step_count: z.number().nullable(),
  created_at: z.string(),
}).passthrough()

const VersionBugSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: z.string(),
  status: z.string(),
  polish_category: z.string().nullable(),
  replay_recording_id: z.string().nullable(),
  test_run_id: z.string().nullable(),
  discovered_at: z.string(),
  version_id: z.string().nullable(),
  is_duplicate: z.boolean(),
}).passthrough()

const VersionExplorationSchema = z.object({
  id: z.string(),
  prompt: z.string(),
  status: z.string(),
  journeys_created: z.number(),
  journeys_updated: z.number(),
  created_at: z.string(),
  journeys: z.array(z.object({
    journey_id: z.string(),
    journey_name: z.string().nullable(),
    action: z.enum(['created', 'updated']),
    step_count: z.number().nullable(),
  }).passthrough()),
}).passthrough()

const FingerprintAssetSchema = z.object({
  url: z.string(),
  type: z.enum(['js', 'css']),
  hash: z.string(),
  bytes: z.number(),
}).passthrough()

const DeployedAppFingerprintSchema = z.object({
  url: z.string(),
  fetchedAt: z.string(),
  assets: z.array(FingerprintAssetSchema),
  assetCount: z.number(),
  totalBytes: z.number(),
  combinedHash: z.string(),
}).passthrough()

export const VersionSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  git_sha: z.string(),
  branch_name: z.string(),
  deployed_url: z.string().nullable(),
  change_description: z.string().nullable(),
  status: z.string(),
  timestamp: z.string(),
  journeys_created: z.number(),
  journeys_updated: z.number(),
  journeys_deleted: z.number(),
  replay_recording_id: z.string().nullable().optional(),
  is_main_branch: z.boolean().optional(),
  deployed_app_fingerprint: DeployedAppFingerprintSchema.nullable().optional(),
  created_at: z.string(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  logs: z.array(VersionLogSchema).optional(),
  mcp_errors: z.array(z.unknown()).optional(),
  journey_changes: z.array(VersionJourneyChangeSchema).optional(),
  explorations: z.array(VersionExplorationSchema).optional(),
  bugs: z.array(VersionBugSchema).optional(),
  task_id: z.string().nullable().optional(),
}).passthrough()

const MonitorRunSchema = z.object({
  id: z.string(),
  status: z.enum(['monitoring', 'detected', 'expired', 'failed']),
  trigger_sha: z.string().nullable(),
  detected_sha: z.string().nullable(),
  detected_version_id: z.string().nullable(),
  checks: z.number(),
  last_error: z.string().nullable(),
  started_at: z.string(),
  last_checked_at: z.string().nullable(),
  deadline: z.string(),
  ended_at: z.string().nullable(),
}).passthrough()

export const PaginatedVersionsResponseSchema = z.object({
  items: z.array(VersionSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  monitor: z.object({
    active: MonitorRunSchema.nullable(),
    runs: z.array(MonitorRunSchema),
  }).optional(),
}).passthrough()

export const JourneyBugsResponseSchema = z.object({
  bugs: z.array(z.object({
    id: z.string(),
    title: z.string(),
    severity: z.string(),
    status: z.string(),
    polish_category: z.string().nullable(),
    replay_recording_id: z.string().nullable(),
    test_run_id: z.string().nullable(),
    discovered_at: z.string(),
    is_duplicate: z.boolean().optional(),
  }).passthrough()),
}).passthrough()

export const ProjectUserSessionSchema = z.object({
  session_id: z.string(),
  external_session_url: z.string(),
  first_received_at: z.string(),
  last_received_at: z.string(),
  journey_status: z.enum(['inspecting_session', 'authoring_journey', 'ready', 'empty', 'failed', 'blocked']).nullable(),
  journey_error: z.string().nullable(),
  journey_blocked_reason: z.enum(['fullstory-session-inaccessible']).nullable().default(null),
  journey_id: z.string().nullable(),
  authoring_warnings: z.array(z.string()),
  journey_requested_at: z.string().nullable(),
  journey_completed_at: z.string().nullable(),
  authoring_task_id: z.string().nullable(),
  network_exchange_count: z.number().int().nonnegative().default(0),
  interaction_count: z.number().int().nonnegative().nullable().default(null),
  user_email: z.string().email().nullable().default(null),
}).passthrough()

export const ProjectUserSessionsResponseSchema = z.object({
  sessions: z.array(ProjectUserSessionSchema),
}).passthrough()

const UserSessionCheckpointSchema = z.object({
  source_event_index: z.number().int().nullable(), source_timestamp: z.number(), source_kind: z.string(),
  tree: z.string(), tokens: z.array(z.string()), dom: z.string().optional(),
  screenshot: z.object({
    url: z.string().url(), key: z.string(), mime_type: z.enum(['image/png', 'image/jpeg', 'image/webp']),
    bytes: z.number().int().positive(), sha256: z.string(),
  }).nullable().optional(),
})
const CapturedUserSessionSchema = z.object({
  version: z.literal(1),
  source: z.object({ kind: z.string(), session_url: z.string().url() }),
  target_url: z.string().url(),
  events: z.array(z.object({
    index: z.number().int(), timestamp: z.number(), kind: z.string(), label: z.string(),
    selector: z.string().optional(), value: z.string().optional(), url: z.string().optional(),
    checkpoint: UserSessionCheckpointSchema, interaction: InteractionSchema.optional(),
  })),
  interaction_events: z.array(z.object({
    index: z.number().int(), timestamp: z.number(), kind: z.string(), label: z.string(),
    selector: z.string().optional(), value: z.string().optional(), url: z.string().optional(),
    checkpoint: UserSessionCheckpointSchema, interaction: InteractionSchema.optional(),
  })).optional(),
  terminal_checkpoint: UserSessionCheckpointSchema,
  network: z.array(z.unknown()),
})
const UserSessionComparisonBaseSchema = z.object({
  replay_observation_id: z.string().uuid().optional(),
  checkpoint: z.enum(['event', 'stream-end']), step_index: z.number().int().nullable(),
  source_event_index: z.number().int().nullable(), source_timestamp: z.number(),
  matched_against_source_event_index: z.number().int().nullable().optional(),
  expected_tree: StoredComparisonTreeSchema, actual_tree: StoredComparisonTreeSchema, similarity: z.number(),
  missing: z.array(z.string()), added: z.array(z.string()), matched: z.boolean(),
})
const UserSessionComparisonPointSchema = UserSessionComparisonBaseSchema.extend({
  skipped_reason: z.string().optional(),
  post_recovery: UserSessionComparisonBaseSchema.optional(),
})
export const ProjectUserSessionDetailResponseSchema = z.object({
  session: z.object({
    session_id: z.string(), external_session_url: z.string().url(),
    first_received_at: z.string(), last_received_at: z.string(),
  }),
  journeys: z.array(z.object({ id: z.string(), name: z.string(), created_at: z.string() })),
  selected_journey: z.object({
    id: z.string(), name: z.string(), created_at: z.string(), source_version_id: z.string().nullable(),
    source_version_number: z.number().nullable(), captured_session: CapturedUserSessionSchema.nullable(),
  }).nullable(),
  latest_run: z.object({
    id: z.string(), status: z.string(), started_at: z.string(), completed_at: z.string().nullable(),
    replay_recording_id: z.string().nullable(), journey_version_id: z.string().nullable(),
    version_number: z.number().nullable(), actions: z.array(z.unknown()).nullable(),
    comparison: z.array(UserSessionComparisonPointSchema).transform(decodeComparisonTrees),
    network_warnings: z.array(z.object({
      kind: z.enum(['network-unexpected', 'network-request-mismatch', 'network-missing', 'network-param-substituted', 'network-request-body-unreadable', 'network-body-truncated', 'network-entity-mismatch', 'network-configured', 'network-unsupported', 'network-retained']),
      step_index: z.number().int().nullable(), method: z.string(), url: z.string(), detail: z.string(),
    })),
  }).nullable(),
}).passthrough()

export const ProjectUserSessionsPresenceResponseSchema = z.object({
  has_sessions: z.boolean(),
}).passthrough()

export const CreateProjectUserSessionJourneyResponseSchema = z.object({
  status: z.string().optional(),
  started: z.boolean().optional(),
  journey: z.object({ id: z.string() }).passthrough().nullable().optional(),
}).passthrough()

export const ProjectEnvironmentSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  name: z.string(),
  kind: z.enum(['production', 'staging', 'development', 'preview', 'local']),
  target_url: z.string().nullable(),
  testing_guidelines: z.string().nullable(),
  environment_variables: z.array(z.object({ name: z.string(), init: z.string().optional() })).nullable(),
  trigger_type: z.enum(['manual', 'schedule', 'github-app', 'github-action']),
  trigger_config: z.record(z.string(), z.unknown()),
  last_triggered_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  excluded_journey_ids: z.array(z.string()),
}).passthrough()

export const ProjectEnvironmentsResponseSchema = z.object({
  environments: z.array(ProjectEnvironmentSchema),
})

export const ProjectEnvironmentMutationResponseSchema = z.object({
  environment: ProjectEnvironmentSchema,
  excluded_journey_ids: z.array(z.string()),
})

export const DeleteProjectEnvironmentResponseSchema = z.object({ success: z.literal(true) })
